import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { getCaseStudyBySlug } from '@/data/case-studies';
import { articleSchema, breadcrumbListSchema } from '@/lib/structured-data';
import { canonical } from '@/lib/seo';
import { SITE_URL } from '@/lib/seo';

// Wrap raw HTML strings so footnote <sup> tags become anchor links and
// stat spans get our brand pill styling, without losing the verbatim copy.
const RichHtml = ({ html, className = '' }: { html: string; className?: string }) => {
  const enriched = html
    .replace(
      /<sup data-fn="(\d+)">(\d+)<\/sup>/g,
      '<sup class="text-fordje-blue font-semibold ml-0.5"><a href="#source-$1" class="no-underline hover:underline">$2</a></sup>'
    )
    .replace(
      /<span data-stat>([^<]+)<\/span>/g,
      '<span class="inline-flex items-baseline rounded-md bg-fordje-blue/10 px-1.5 py-0.5 font-mono text-[0.92em] font-medium text-fordje-navy whitespace-nowrap">$1</span>'
    );
  return <span className={className} dangerouslySetInnerHTML={{ __html: enriched }} />;
};

const CaseStudyPost = () => {
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const slug = routeSlug ?? 'discovery-tax';
  const study = getCaseStudyBySlug(slug);

  if (!study) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container-custom section-padding">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Case Study Not Found</h1>
            <p className="mb-8">The case study you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/blog')}>Back to Insights</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const url = canonical(`/report/${study.slug}`);
  const metaTitle = `${study.title} | Fordje Case Study`;
  const caseStudyOgImage = `${SITE_URL}/og-case-study-${study.slug}.png`;

  const structured = articleSchema({
    type: 'Article',
    title: study.title,
    description: study.metaDescription,
    slug: study.slug,
    pathPrefix: '/report',
    coverImage: caseStudyOgImage,
    datePublished: study.date,
    author: { name: 'Fordje', title: 'Regulatory Intelligence' },
    categories: ['Case Study', 'C&I Solar'],
    keywords: study.seoKeywords,
    readTimeMinutes: study.readTime,
  });

  const breadcrumb = breadcrumbListSchema([
    { name: 'Home', path: '/' },
    { name: 'Insights', path: '/blog' },
    { name: study.title, path: `/report/${study.slug}` },
  ]);

  // Split title around the highlighted dollar amount.
  const [titleLead, titleTail] = study.title.split(study.titleHighlight);
  const dekParts = study.dek.split(study.dekBold);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={study.metaDescription} />
        <meta name="keywords" content={study.seoKeywords.join(', ')} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={study.title} />
        <meta property="og:description" content={study.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={caseStudyOgImage} />
        <meta property="og:site_name" content="Fordje" />
        <meta property="article:published_time" content={study.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={study.title} />
        <meta name="twitter:description" content={study.metaDescription} />
        <meta name="twitter:image" content={caseStudyOgImage} />
        <script type="application/ld+json">{JSON.stringify(structured)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <Header />

      <main className="flex-grow pt-28 md:pt-36 pb-16">
        <article className="container-custom max-w-3xl mx-auto">
          {/* Masthead */}
          <div className="flex justify-between items-center pb-4 mb-10 border-b border-slate-200">
            <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] text-fordje-navy">
              <span className="h-2 w-2 rounded-full bg-fordje-blue" />
              <span className="font-semibold">FORDJE</span>
            </div>
            <span className="font-mono text-[11px] tracking-[0.06em] text-slate-500">
              {study.series}
            </span>
          </div>

          {/* Hero */}
          <p className="font-mono text-[11.5px] uppercase tracking-[0.16em] text-fordje-blue font-medium mb-5">
            {study.eyebrow}
          </p>
          <h1 className="font-ubuntu font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight text-fordje-navy mb-6 max-w-[15ch]">
            {titleLead}
            <span className="text-fordje-blue">{study.titleHighlight}</span>
            {titleTail}
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-6 max-w-[60ch]">
            {dekParts[0]}
            <strong className="text-fordje-navy font-semibold">{study.dekBold}</strong>
            {dekParts[1]}
          </p>

          {/* Hero meta */}
          <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500 mb-7 flex flex-wrap gap-x-2 gap-y-1">
            <span className="text-fordje-navy font-semibold">By {study.heroMeta.byline}</span>
            <span className="text-slate-300">·</span>
            <span>{study.heroMeta.date}</span>
            <span className="text-slate-300">·</span>
            <span>{study.heroMeta.readTime}</span>
            <span className="text-slate-300">·</span>
            <span className="normal-case tracking-normal text-slate-500">Based on {study.heroMeta.methodology}</span>
          </div>

          {/* Hero CTA row */}
          <div className="flex flex-wrap items-center gap-4 mb-12">
            <Link to="/request-demo">
              <Button className="bg-fordje-blue hover:bg-fordje-blue-dark text-white">
                Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a
              href="#the-number"
              className="text-sm font-medium text-fordje-navy hover:text-fordje-blue transition-colors inline-flex items-center gap-1"
            >
              Jump to the number ↓
            </a>
          </div>

          {/* TL;DR */}
          <aside className="mb-14 rounded-xl border border-slate-200 border-l-[3px] border-l-fordje-blue bg-white px-7 py-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-fordje-blue font-semibold mb-4">
              {study.tldr.title}
            </div>
            <ul className="space-y-3">
              {study.tldr.points.map((pt, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-[1.6] text-slate-700">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-fordje-blue flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: pt }} />
                </li>
              ))}
            </ul>
          </aside>

          {/* Lede */}
          <section className="space-y-4 mb-14">
            {study.lede.map((p, i) => (
              <p key={i} className="text-[16px] leading-[1.7] text-slate-700">
                <RichHtml html={p.html} />
              </p>
            ))}
          </section>

          {/* Anatomy */}
          <section className="mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium">
                Anatomy of the Discovery Tax
              </span>
              <span className="flex-1 h-px bg-slate-200" />
            </div>
            <p className="text-slate-700 leading-relaxed mb-6">
              <RichHtml html={study.anatomyIntro} />
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {study.phases.map((ph) => (
                <div
                  key={ph.n}
                  className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 pt-6 flex flex-col"
                >
                  <span className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-fordje-blue to-fordje-blue/40" />
                  <div className="font-mono text-[10px] tracking-[0.1em] text-fordje-blue font-medium">
                    PHASE {ph.n}
                  </div>
                  <div className="font-ubuntu font-bold text-xl text-fordje-navy mt-1 mb-2">
                    {ph.title}
                  </div>
                  <div className="inline-flex self-start items-baseline rounded-md bg-fordje-blue/10 px-2 py-0.5 font-mono text-[12px] font-semibold text-fordje-navy mb-3">
                    {ph.stat}
                  </div>
                  <p className="text-[13px] leading-[1.55] text-slate-600">{ph.body}</p>
                </div>
              ))}
            </div>

            {/* Hero pull quote */}
            {study.heroQuote && (
              <figure className="relative mt-10 rounded-xl border border-slate-200 bg-white pl-6 pr-7 py-7 md:pl-8 md:pr-10 md:py-9">
                <span className="absolute left-0 top-4 bottom-4 w-1 rounded bg-fordje-blue" />
                <blockquote className="font-ubuntu italic text-2xl md:text-[28px] leading-[1.3] text-fordje-navy mb-4">
                  "{study.heroQuote.quote}"
                </blockquote>
                <figcaption className="font-mono text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  <span className="text-fordje-navy font-semibold">{study.heroQuote.cite}</span>
                  <span className="text-slate-300 mx-2">·</span>
                  <span className="normal-case tracking-normal">{study.heroQuote.detail}</span>
                </figcaption>
              </figure>
            )}

            {/* Supporting quotes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              {study.quotes.map((q, i) => (
                <figure key={i} className="relative pl-4">
                  <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded bg-fordje-blue/60" />
                  <blockquote className="font-ubuntu italic text-[16px] leading-snug text-fordje-navy mb-2">
                    "{q.quote}"
                  </blockquote>
                  <figcaption className="font-mono text-[10px] uppercase tracking-wide text-slate-500">
                    {q.cite}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>

          {/* The Number */}
          <section id="the-number" className="mb-14 scroll-mt-28">
            <div className="rounded-2xl border border-slate-200 bg-white px-7 py-8 md:px-9 md:py-10 shadow-[0_8px_28px_rgba(16,24,40,0.05)]">
              <div className="flex items-center gap-3 mb-5">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium">
                  THE NUMBERS
                </span>
                <span className="flex-1 h-px bg-slate-200" />
              </div>
              <div className="font-ubuntu font-bold text-4xl md:text-5xl leading-none tracking-tight text-fordje-navy mb-1">
                <span className="text-fordje-blue">$16,500</span>{' '}
                <span className="text-xl md:text-2xl text-slate-500 font-medium">/ month</span>
                <span className="mx-3 text-slate-300">·</span>
                <span className="text-fordje-blue">$540</span>{' '}
                <span className="text-xl md:text-2xl text-slate-500 font-medium">/ day</span>
              </div>
              <div className="font-mono text-[11px] tracking-wide text-slate-500 mb-5">
                {study.numberBlock.since}
              </div>
              <div className="space-y-4 text-[15px] leading-[1.7] text-slate-700">
                {study.numberBlock.paragraphs.map((p, i) => (
                  <p key={i}>
                    <RichHtml html={p} />
                  </p>
                ))}
              </div>
            </div>
          </section>

          {/* Boundary */}
          <section className="mb-14">
            <div className="rounded-xl border border-slate-200 border-l-[3px] border-l-fordje-navy bg-white px-7 py-7">
              <div className="flex items-center gap-3 mb-3">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-fordje-navy font-semibold">
                  {study.boundary.kicker}
                </span>
                <span className="flex-1 h-px bg-slate-200" />
              </div>
              <p className="text-slate-700 leading-relaxed">
                <RichHtml html={study.boundary.html} />
              </p>
            </div>
          </section>

          {/* Close — editorial conclusion, consistent with page tone */}
          <section className="mb-14">
            <div className="rounded-xl border border-slate-200 border-l-[3px] border-l-fordje-blue bg-white px-7 py-8 md:px-10 md:py-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-fordje-blue font-semibold">
                  {study.close.eyebrow}
                </span>
                <span className="flex-1 h-px bg-slate-200" />
              </div>

              <h2 className="font-ubuntu font-bold text-xl md:text-2xl leading-[1.25] text-fordje-navy mb-4 max-w-[52ch]">
                {study.close.headline}
              </h2>
              <p className="text-[15px] leading-[1.7] text-slate-700 max-w-[60ch] mb-6">
                {study.close.body}
              </p>

              {/* Proof points — inline, restrained */}
              <div className="flex flex-wrap gap-x-8 gap-y-3 mb-8">
                {study.close.proofPoints.map((pp, i) => (
                  <div key={i} className="flex items-baseline gap-2">
                    <span className="font-ubuntu font-bold text-lg text-fordje-blue">{pp.stat}</span>
                    <span className="text-[13px] text-slate-500">{pp.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-slate-100">
                <Link to={study.close.ctaHref}>
                  <Button className="bg-fordje-blue hover:bg-fordje-blue-dark text-white text-sm px-5">
                    {study.close.ctaLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Sources */}
          <section className="border-t border-slate-200 pt-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium">
                Sources
              </span>
              <span className="flex-1 h-px bg-slate-200" />
            </div>
            <ol className="md:columns-2 md:gap-x-10 list-decimal pl-5 marker:text-slate-400">
              {study.sources.map((s, i) => (
                <li
                  id={`source-${i + 1}`}
                  key={i}
                  className="text-[11px] leading-[1.55] text-slate-500 mb-2 break-inside-avoid scroll-mt-28"
                >
                  <span dangerouslySetInnerHTML={{ __html: s }} />
                </li>
              ))}
            </ol>
          </section>

          <div className="flex justify-between items-center mt-10 pt-5 border-t border-slate-200 font-mono text-[11px] tracking-wide text-slate-600">
            <span className="flex items-center gap-2 text-fordje-navy font-semibold tracking-[0.14em]">
              <span className="h-1.5 w-1.5 rounded-full bg-fordje-blue" />
              FORDJE
            </span>
            <span>Regulatory intelligence for clean-tech development</span>
          </div>

          <div className="mt-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-fordje-blue transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to all Insights
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default CaseStudyPost;
